
import React, { useState } from 'react';
import faqimg from "../../assets/images/faq.png";
import './Faq.scss';

const sections = [
  {
    title: 'About The Taurian',
    items: [
      {
        question: 'What is The Taurian?',
        body: [
          'The Taurian is more than a membership — it’s a community built to help you discover what’s happening around you and experience more of the automotive world.',
          'From rides, races, track days and training to family activities and events, we bring opportunities together in one place and work with our partners to make experiences that once felt out of reach more accessible.',
          'We’re building a community where people can connect, ride, learn, save and create memories together, while working towards a bigger vision of better riding, training and motorsport infrastructure across cities and regions.',
          'Because life is full of things we say we’ll do someday — we’d rather help you do them now.',
        ],
      },
      {
        question: 'Who can join The Taurian?',
        body: [
          'Anyone who shares the passion for automotive and motorcycling culture can join. You don’t need to own an expensive motorcycle or car — being part of the culture is what matters.',
        ],
      },
      {
        question: 'Is The Taurian only for motorcycle riders?',
        body: [
          'No. The Taurian is built around the wider automotive lifestyle.',
          'Our community includes motorcycle riders, car enthusiasts, automotive fans and people who simply love machines, experiences and the culture surrounding them.',
        ],
      },
      {
        question: 'Do I need to own a motorcycle or car to become a member?',
        body: [
          'No. Membership is about being part of the community, not what you own.',
        ],
      },
    ],
  },
  {
    title: 'Membership',
    items: [
      {
        question: 'What do I get with a Taurian membership?',
        body: [
          'Your membership can give you access to:',
          [
            'Exclusive partner offers and discounts',
            'Member-only events and experiences',
            'Special member pricing',
            'Giveaways and rewards',
            'Community activities and rides',
            'Ride24 app access',
            'Exclusive promotions',
            'Discounted trackdays and automotive training.',
            'Your digital membership card',
            'Member wallet and available rewards',
            'New benefits added throughout the year',
          ],
          'Your benefits may vary depending on your membership tier.',
        ],
      },
      {
        question: 'What are the different membership tiers?',
        body: [
          'The Taurian offers different membership levels designed for different types of members.',
          'Each tier comes with its own combination of benefits, discounts, experiences and privileges.',
          'You can compare the current membership options on our Membership page before joining.',
        ],
      },
      {
        question: 'Can I upgrade my membership?',
        body: [
          'Yes. Where an upgrade is available, you can upgrade to a higher membership tier to access additional benefits and privileges.',
        ],
      },
      {
        question: 'Can I cancel my membership?',
        body: [
          'Yes. You may cancel your membership anytime. No questions asked. But we are improving every aspect of our website to keep this interesting and valuable for our members.',
        ],
      },
      {
        question: 'Does my membership automatically renew?',
        body: [
          'Yes. Membership is renewed monthly. You can find your membership details, expiry etc. under My account → Dashboard → Account. You may cancel auto renewal anytime by cancelling the membership.',
        ],
      },
    ],
  },
  {
    title: 'Member Benefits',
    items: [
      {
        question: 'How do I use my member discounts?',
        body: [
          'After joining, log in to your Taurian account and explore the available partner offers.',
          'Each offer will show the applicable benefit, redemption instructions and any conditions that apply.',
        ],
      },
      {
        question: 'Where can I find my member offers?',
        body: [
          'Your available offers can be accessed from your member dashboard. For this you need to sign up and login to website.',
          'We are continuously adding new partners and benefits to the Taurian ecosystem.',
        ],
      },
      {
        question: 'Can I use every partner offer immediately after joining?',
        body: [
          'Yes. Offers are available once your membership is active. However, individual partner offers may have their own eligibility requirements, exclusions or redemption conditions.',
          'Always check the offer details before using it.',
        ],
      },
      {
        question: 'Do partner discounts apply to everything in a store?',
        body: [
          'Not necessarily.',
          'Each partner decides the terms of its offer. Some offers may apply storewide, while others may apply only to selected products or services.',
          'The specific terms will always be displayed with the offer.',
        ],
      },
    ],
  },
  {
    title: 'Events & Experiences',
    items: [
      {
        question: 'What types of events does The Taurian organise?',
        body: [
          'Our events can include:',
          [
            'Community rides',
            'Automotive experiences',
            'Track experiences',
            'Meetups',
            'Workshops',
            'Brand experiences',
            'Women-only events',
            'Member-only experiences',
            'Giveaways',
            'Track days',
            'Training',
            'Special automotive events',
          ],
          'New experiences will be added as the community grows.',
        ],
      },
      {
        question: 'Do members get discounts on events?',
        body: [
          'Selected events may offer special pricing or benefits based on your membership tier.',
          'The applicable pricing will be displayed on the individual event page.',
        ],
      },
      {
        question: 'Are all events included with membership?',
        body: [
          'No.',
          'Some events may be free for eligible members, while others may require registration or a separate ticket.',
          'The event page will clearly explain what is included.',
        ],
      },
      {
        question: 'Can I bring a friend to a Taurian event?',
        body: [
          'This depends on the individual event.',
          'Some events may be member-only, while others may allow guests or have separate guest tickets.',
          'Check the event details before registering.',
        ],
      },
    ],
  },
  {
    title: 'Giveaways & Rewards',
    items: [
      {
        question: 'Do Taurian members get access to giveaways?',
        body: [
          'Eligible members may receive access to selected Taurian giveaways and member rewards.',
          'Giveaway eligibility, entry mechanics, prizes and terms will be clearly provided for each individual promotion.',
        ],
      },
      {
        question: 'How do I participate in a giveaway?',
        body: [
          'If you are eligible, participation will be handled through your Taurian account according to the rules of that particular promotion.',
          'Some promotions may automatically provide eligible members with an entry, while others may require registration.',
          'Always check the specific promotion terms.',
        ],
      },
      {
        question: 'What can I win?',
        body: [
          'Prizes can vary from promotion to promotion and may include automotive products, experiences, accessories, lifestyle products and other exciting prizes.',
          'From time to time, Taurian may also collaborate with brands to create special member experiences and prizes.',
        ],
      },
      {
        question: 'Are giveaways included with my membership?',
        body: [
          'Eligibility depends on the particular promotion and your membership status.',
          'Always refer to the official terms and conditions for the promotion.',
        ],
      },
      {
        question: 'Where can I see the giveaway rules?',
        body: [
          'The applicable terms, eligibility requirements and conditions will be provided on the relevant promotion or giveaway page.',
        ],
      },
    ],
  },
  {
    title: 'My Taurian Account & Wallet',
    items: [
      {
        question: 'What is My Account?',
        body: [
          'My Account is your personal member dashboard.',
          'It gives you a central place to manage your membership and access your available member benefits.',
        ],
      },
      {
        question: 'What is the Taurian Wallet?',
        body: [
          'Your Wallet is designed to bring your digital membership benefits together in one place.',
          'Depending on your membership and available features, it may include your digital membership card, partner offers, coupons and rewards.',
        ],
      },
      {
        question: 'What is my digital membership card?',
        body: [
          'Your digital membership card verifies your active Taurian membership and may be used when accessing eligible member benefits.',
        ],
      },
      {
        question: 'What are Reward Points?',
        body: [
          'Reward Points are part of The Taurian’s rewards ecosystem.',
          'Eligible activities or purchases may earn points according to the applicable reward rules. Available points, redemption options and expiry conditions will be displayed in your account where applicable.',
        ],
      },
    ],
  },
  {
    title: 'Payments & Support',
    items: [
      {
        question: 'Is my membership payment secure?',
        body: [
          'Payments are processed with Razorpay. Payments are processed using applicable payment-security measures. We do not store your payments details like card/UPI details etc.',
        ],
      },
      {
        question: 'I paid for membership but cannot access my account. What should I do?',
        body: [
          'First, check that you are logging in with the email address used during registration.',
          'If you still cannot access your membership, contact The Taurian support team and provide your registered email address and payment details.',
        ],
      },
      {
        question: 'I cannot find an offer or benefit I was expecting. What should I do?',
        body: [
          'Benefits can change based on membership tier, availability and partner terms.',
          'Check your membership status and the current offer details in your dashboard. If you believe you should have access and cannot see the benefit, contact our support team.',
        ],
      },
      {
        question: 'How do I contact The Taurian?',
        body: [
          'For membership, account, partner-benefit or event-related questions, contact us through the official support channels provided on the website.',
        ],
      },
    ],
  },
  {
    title: 'Still have questions?',
    note: [
      'We’re building The Taurian with our members — and we’re always adding new experiences, partners and benefits.',
      'Have a question we haven’t answered? Get in touch with our team.',
    ],
  },
];

const AnswerBody = ({ body }) =>
  body.map((block, index) =>
    Array.isArray(block) ? (
      <ul key={index}>
        {block.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    ) : (
      <p key={index}>{block}</p>
    )
  );

const AccordionItem = ({ title, items, note }) => {
  const [isActive, setIsActive] = useState(false);

  const handleToggle = () => {
    setIsActive(!isActive);
  };

  return (
    <div className={`accordion-item ${isActive ? "active" : ""}`}>
      <div className="accordion-header" onClick={handleToggle}>
        <div>{title}</div>
        <div className="icon">{isActive ? "-" : "+"}</div>
      </div>
      {isActive && (
        <div className="accordion-content">
          {note && (
            <div className="faq-note">
              {note.map((text) => (
                <p key={text}>{text}</p>
              ))}
            </div>
          )}
          {items?.map((item) => (
            <div className="faq-qa" key={item.question}>
              <h3 className="faq-qa-q">{item.question}</h3>
              <div className="faq-qa-a">
                <AnswerBody body={item.body} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// theme="dark" opts into the dark styling in Faq.scss; every other caller
// keeps the original light block.
const Faq = ({ theme }) => {
    return (

        <div className={`container-fluid fallback-old-style${theme === 'dark' ? ' faq-section-dark' : ''}`}>
      <div className="container">
        <div className="row pt-50 pb-50">
          <div className="col-lg-60 col-md-100 p-10 ">
            <div className="pt-40 pb-40">
              <img src={faqimg} alt="Rider in a helmet" />
            </div>
          </div>

          <div className="col-lg-40 col-md-100 p-10 ">
            <div className="faq pt-40 pb-40">
              <h1>
                More Than <br />
                A Membership
              </h1>
              <p>A community built around the experiences we should never miss.</p>
      <div className="accordion">
        {sections.map((section) => (
          <AccordionItem
            key={section.title}
            title={section.title}
            items={section.items}
            note={section.note}
          />
        ))}
      </div>
      </div>
          </div>
        </div>
      </div>
    </div>
    );
  };
  

export default Faq;
